// @ts-nocheck
import { MinMaxValue } from "../../../types/filters";
import { HgCountry } from "../../../types/taxonomy";
import { HgNaics } from "../../../types/taxonomy";
import { HgSpendCategory } from "../../../types/taxonomy";
import { CompanyFilter, CompanyMatchFilter } from "../companyFilter";
import type {
  ModelScoreInput,
  ModelScoreFilterInput,
  SpendCategorySpendBreakdown,
} from "@/generated/company-search";

/** Args threaded through to the 5 market-intelligence queries that support V2
 * ("model tier") scoring narrowing — mirrors what opportunityGeneratorSearch
 * already accepts. NOT supported by globalMarketAnalysisSpendCategoryBreakdown
 * (out of scope). */
export interface ModelScoreQueryArgs {
  tenantId?: string;
  modelScores?: ModelScoreInput[];
  modelScoreFilter?: ModelScoreFilterInput[];
}

export interface CustomSpendCategory {
  name: string;
  categories: {
    id: string;
    weight: number;
  }[];
  spendType?: "IT" | "AI";
}

export type GMA_GEO_SPEND_PAYLOAD = {
  globalMarketAnalysisGeopoliticalRegionBreakdown: {
    companyCount: number;
    totalSpend: number;
    regionId?: string;
    regionName?: string;
  }[];
};

export type EmployeeBreakdownQueryPayload = {
  segmentAnalysisEmployeeRangeBreakdown: {
    companyCount: number;
    totalSpend: number;
    range: MinMaxValue;
  }[];
};

export type MarketSummaryPayload = {
  totalMarketSummary: {
    companyCount: number;
    totalSpend: number;
  };
};

export type MarketSummaryVariables = {
  companyFilters?: CompanyFilter;
  spendCategoryIds?: string[];
  customSpendCategory?: CustomSpendCategory;
  localizeInstalls?: boolean;
  companyMatchFile?: CompanyMatchFilter;
} & ModelScoreQueryArgs;

export type SpendCategoryBreakdownPayload = {
  globalMarketAnalysisSpendCategoryBreakdown: Array<SpendCategorySpendBreakdown | null> | null;
};

export type SpendCategoryBreakdownVariables = {
  companyFilters?: CompanyFilter;
  spendCategoryIds?: string[];
  localizeInstalls?: boolean;
  companyMatchFile?: CompanyMatchFilter;
} & ModelScoreQueryArgs;

export type RevenueBreakdownQueryPayload = {
  segmentAnalysisRevenueRangeBreakdown: {
    companyCount: number;
    totalSpend: number;
    range: MinMaxValue;
  }[];
};

export interface SegmentAnalysisNaicsBreakdown {
  companyCount: number;
  totalSpend: number;
  naicsId: string;
}

export interface NAICSBreakdownQueryPayload {
  segmentAnalysisNaicsBreakdown: Array<SegmentAnalysisNaicsBreakdown>;
}

export interface HgProductCategory {
  id: string;
  code?: string;
  name: string;
  parentId?: string;
  nameTree: string[];
  spendCategoryId?: string;
}

export interface ProductCategoryQueryPayload {
  category: {
    data: HgProductCategory[];
  };
}

interface BaseBreakdownResult {
  spend: number;
  companies: number;
}

export interface SpendBreakdownResult extends BaseBreakdownResult {
  dimension?: string;
  id?: string | number;
  href?: string;
  meta?: number;
}
export interface CountrySpendBreakdown {
  __typename: "CountrySpendBreakdown";
  averageKnownSpend?: number;
  companyCount: number;
  countryId?: string;
  countryName?: string;
  totalSpend: number;
}

export interface CountryBreakdownPayload {
  segmentAnalysisCountryBreakdown: Array<CountrySpendBreakdown>;
}

export interface CountryBreakdownVariables extends ModelScoreQueryArgs {
  companyFilters?: CompanyFilter;
  localizeInstalls?: boolean;
  spendCategoryIds?: Array<string>;
  customSpendCategory?: CustomSpendCategory;
  companyMatchFile?: CompanyMatchFilter;
  /** Row-count cap, forwarded as-is to hermes — the only Segment Analysis
   * breakdown with a server-side limit. Omitted (undefined) behaves exactly
   * like every caller before this field existed (hermes' own default of 10),
   * e.g. Market Landscape; Market Sizing's charts pass 20. */
  limit?: number;
}

export interface CustomEmployeeRange {
  label: string;
  min: number | null;
  max: number | null;
}

export interface NAICSBreakdownQueryVariables extends ModelScoreQueryArgs {
  companyFilters?: CompanyFilter;
  customSpendCategory?: CustomSpendCategory;
  localizeInstalls?: boolean;
  spendCategoryIds?: Array<string>;
  companyMatchFile?: CompanyMatchFilter;
}

export interface MarketSpendData {
  category?: HgSpendCategory;
  naics_2012?: HgNaics;
  country?: HgCountry;
  companyCount: number;
  sumRelativeValues: number;
  region?: {
    id: string;
    name: "APAC" | "AMER" | "EMEA";
  };
  hasSpendCompanyCount?: number;
}

export interface MarketTimeSeriesBreakdown {
  hits: number;
  monthStartDate: string;
}

export interface TrendingDataPayload {
  marketTimeSeriesProductBreakdown: Array<
    MarketTimeSeriesBreakdown & { productId: number }
  >;
  marketTimeSeriesVendorBreakdown: Array<
    MarketTimeSeriesBreakdown & { vendorId: number }
  >;
  marketTimeSeriesProductCategoryBreakdown: Array<
    MarketTimeSeriesBreakdown & { categoryId: string }
  >;
  marketTimeSeriesProductAttributeBreakdown: Array<
    MarketTimeSeriesBreakdown & { attributeId: number }
  >;
}

export interface TrendingDataVariables {
  companyFilters?: CompanyFilter;
  lookBackYears?: TrendingDataPeriodOption;
  period: TrendingDataSegmentOption;
  metric: TrendingDataMetricOption;
  productIds?: Array<number>;
  vendorIds?: Array<number>;
  categoryIds?: Array<string>;
  attributeIds?: Array<number>;
  localizeInstalls: boolean;
  companyMatchFile?: CompanyMatchFilter;
}

export enum TrendingDataMetricOption {
  FirstVerified = "FIRST_VERIFIED",
}

export enum TrendingDataPeriodOption {
  OneYear = 1,
  TwoYears = 2,
  ThreeYears = 3,
  FourYears = 4,
  FiveYears = 5,
}

export enum TrendingDataSegmentOption {
  Month = "MONTH",
  Quarter = "QUARTER",
  Year = "YEAR",
}
